const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export class APIError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.statusCode = status; // Add alias for statusCode
    this.data = data;
    this.rateLimitInfo = data?.rateLimitInfo; // Extract rate limit info
  }
}

export const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  const token = localStorage.getItem('token');
  if (token) {
    defaultHeaders.Authorization = `Bearer ${token}`;
  }

  const config = {
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, config);
    
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      throw new APIError(
        data?.error || data?.message || `HTTP ${response.status}`,
        response.status,
        data
      );
    }

    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    
    // Network or other errors
    throw new APIError(
      'Network error or server unavailable',
      0,
      { originalError: error.message }
    );
  }
};

// Utility functions for common API calls
export const api = {
  // Auth
  auth: {
    login: (credentials) => 
      apiRequest('/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
      }),
    
    me: () => 
      apiRequest('/auth/me'),
    
    password: (passwords) => 
      apiRequest('/auth/password', {
        method: 'POST',
        body: JSON.stringify(passwords),
      }),
    
    resetPassword: (userId, newPassword) => 
      apiRequest('/auth/password', {
        method: 'POST',
        body: JSON.stringify({
          newPassword,
          confirmPassword: newPassword,
          targetUserId: userId
        }),
      }),

    // 2FA endpoints
    setup2FA: () => apiRequest('/auth/setup-2fa', { method: 'POST' }),
    enable2FA: (token) => apiRequest('/auth/enable-2fa', { method: 'POST', body: JSON.stringify({ token }) }),
    disable2FA: (token) => apiRequest('/auth/disable-2fa', { method: 'POST', body: JSON.stringify({ token }) }),
    verify2FA: (token, tempToken) => apiRequest('/auth/verify-2fa', { method: 'POST', body: JSON.stringify({ token, tempToken }) }),
    verifyBackupCode: (email, backupCode) => apiRequest('/auth/verify-backup-code', { method: 'POST', body: JSON.stringify({ email, backupCode }) }),
    adminReset2FA: (userId, token) => apiRequest(`/auth/admin/reset-2fa/${userId}`, { method: 'POST', body: JSON.stringify({ token }) }),
  },

  // Users
  users: {
    list: () => 
      apiRequest('/auth/users'),
    
    create: (userData) => 
      apiRequest('/auth/users', {
        method: 'POST',
        body: JSON.stringify(userData),
      }),
    
    update: (id, userData) => 
      apiRequest(`/auth/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(userData),
      }),
  },

  // Sites
  sites: {
    list: () => 
      apiRequest('/sites'),
    
    listForUser: (userId, params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return apiRequest(`/sites/for-user/${userId}${queryString ? `?${queryString}` : ''}`);
    },
    
    get: (id) => 
      apiRequest(`/sites/${id}`),
    
    create: (siteData) => 
      apiRequest('/sites', {
        method: 'POST',
        body: JSON.stringify(siteData),
      }),
    
    update: (id, siteData) => 
      apiRequest(`/sites/${id}`, {
        method: 'PUT',
        body: JSON.stringify(siteData),
      }),
    
    delete: (id) => 
      apiRequest(`/sites/${id}`, {
        method: 'DELETE',
      }),
    
    getPermissions: (id) => 
      apiRequest(`/sites/${id}/permissions`),
    
    addPermission: (id, userId) => 
      apiRequest(`/sites/${id}/permissions`, {
        method: 'POST',
        body: JSON.stringify({ user_id: userId }),
      }),
    
    removePermission: (id, userId) => 
      apiRequest(`/sites/${id}/permissions/${userId}`, {
        method: 'DELETE',
      }),
  },

  // Purge
  purge: {
    // Accepts a single URL/path or an array of them. The server derives the
    // surrogate key for each and refuses whole-domain keys unless confirmed.
    url: (siteId, urls, { confirmWholeDomain = false } = {}) =>
      apiRequest('/purge/url', {
        method: 'POST',
        body: JSON.stringify({
          siteId,
          urls: Array.isArray(urls) ? urls : [urls],
          confirmWholeDomain,
        }),
      }),
    
    surrogateKey: (siteId, surrogateKey) => 
      apiRequest('/purge/surrogate-key', {
        method: 'POST',
        body: JSON.stringify({ siteId, surrogateKey }),
      }),
    
    all: (siteId) => 
      apiRequest('/purge/all', {
        method: 'POST',
        body: JSON.stringify({ siteId }),
      }),
      
      debugHeaders: (siteId, url) =>
        apiRequest('/purge/debug/headers', {
          method: 'POST',
          body: JSON.stringify({ siteId, url }),
        }),
      
      scanSurrogateKey: (siteId) =>
        apiRequest('/purge/scan-surrogate-key', {
          method: 'POST',
          body: JSON.stringify({ siteId }),
        }),
      
      scanSitemap: (siteId, subpath = '') => 
        apiRequest('/purge/debug/sitemap', {
          method: 'POST',
          body: JSON.stringify({ siteId, subpath }),
        }),
  },

  // Audit
  audit: {
    list: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return apiRequest(`/audit${queryString ? `?${queryString}` : ''}`);
    },
    
    get: (id) => 
      apiRequest(`/audit/${id}`),
    
    stats: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return apiRequest(`/audit/stats/summary${queryString ? `?${queryString}` : ''}`);
    },
    
    analyzerLogs: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return apiRequest(`/audit/analyzer${queryString ? `?${queryString}` : ''}`);
    },
  },

  // Fastly API helpers
  fastly: {
    getServices: () =>
      apiRequest('/fastly/services'),
    
    testToken: (fastlyToken) =>
      apiRequest('/fastly/test-token', {
        method: 'POST',
        body: JSON.stringify({ fastly_token: fastlyToken }),
      }),
    
    getServiceDetails: (fastlyToken, serviceId) =>
      apiRequest('/fastly/service-details', {
        method: 'POST',
        body: JSON.stringify({ fastly_token: fastlyToken, service_id: serviceId }),
      }),
    
    getServiceDomains: (serviceId) =>
      apiRequest(`/fastly/service/${serviceId}/domains`),
  },

  // Cloudflare purge (experimental)
  cloudflare: {
    purgeUrl: (zoneId, urls) =>
      apiRequest('/cloudflare/purge/url', {
        method: 'POST',
        body: JSON.stringify({ zoneId, urls }),
      }),
    purgeAll: (zoneId) =>
      apiRequest('/cloudflare/purge/all', {
        method: 'POST',
        body: JSON.stringify({ zoneId }),
      }),
    resolveZone: (domain) =>
      apiRequest(`/cloudflare/zones/resolve?domain=${encodeURIComponent(domain)}`),
    listZones: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return apiRequest(`/cloudflare/zones/db${qs ? `?${qs}` : ''}`);
    },
    syncZones: () =>
      apiRequest('/cloudflare/zones/sync', { method: 'POST' }),
    debugHeaders: (url) =>
      apiRequest('/cloudflare/debug/headers', {
        method: 'POST',
        body: JSON.stringify({ url }),
      }),
    scanSitemap: (zoneName, subpath = '') =>
      apiRequest('/cloudflare/debug/sitemap', {
        method: 'POST',
        body: JSON.stringify({ zoneName, subpath }),
      }),
    purgeHosts: (zoneId, hosts) =>
      apiRequest('/cloudflare/purge/hosts', {
        method: 'POST',
        body: JSON.stringify({ zoneId, hosts }),
      }),
    purgePrefixes: (zoneId, prefixes) =>
      apiRequest('/cloudflare/purge/prefixes', {
        method: 'POST',
        body: JSON.stringify({ zoneId, prefixes }),
      }),
  },

  // Meta
  meta: {
    version: () => apiRequest('/meta/version'),
  },

  // Domain Analyzer - ISP blocking detection
  analyzer: {
    // Get available mobile carriers
    getCarriers: () =>
      apiRequest('/analyzer/carriers'),
    
    // Get all countries with ASN data
    getCountries: () =>
      apiRequest('/analyzer/countries'),
    
    // Get ASNs for a specific country
    getCountryAsns: (countryCode, limit = 50) =>
      apiRequest(`/analyzer/country/${countryCode}/asns?limit=${limit}`),
    
    // Search ASNs by name
    searchAsns: (query, country = null) =>
      apiRequest(`/analyzer/search?q=${encodeURIComponent(query)}${country ? `&country=${country}` : ''}`),
    
    // Run full analysis with specific carriers
    analyze: (url, carriers) =>
      apiRequest('/analyzer/analyze', {
        method: 'POST',
        body: JSON.stringify({ url, carriers }),
      }),
    
    // Quick check with default carriers for a country
    quickCheck: (url, country) =>
      apiRequest('/analyzer/quick-check', {
        method: 'POST',
        body: JSON.stringify({ url, country }),
      }),
    
    // Get screenshot URL - needs auth token
    getScreenshotUrl: (filename) => {
      const token = localStorage.getItem('token');
      // Return the URL - we'll fetch with auth header separately
      return `${API_BASE_URL}/analyzer/screenshot/${encodeURIComponent(filename)}`;
    },
    
    // Fetch screenshot as blob URL (with auth)
    fetchScreenshot: async (filename) => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/analyzer/screenshot/${encodeURIComponent(filename)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return null;
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    },
    
    // Probe available ASNs for a country (returns EventSource URL for SSE)
    probeAsnsStream: (country, rotations = 50) => {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        country,
        rotations: rotations.toString(),
        token // Pass token in query for SSE (EventSource doesn't support headers)
      }).toString();
      return `${API_BASE_URL}/analyzer/probe-asns?${params}`;
    },
  },
};
